import React, { useState } from 'react';
import styled from 'styled-components';
import { Container } from '../styles/GlobalStyles';
import { MapPin, Phone, Mail, Clock, MessageSquare, Users, Instagram, X } from 'lucide-react';
import ContactForm from './ContactForm';

const ContactSectionContainer = styled.section`
  padding: 5rem 0;
  background: linear-gradient(
    135deg,
    #1a202c 0%,
    #2d3748 50%,
    #1a202c 100%
  );
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(139, 0, 0, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 215, 0, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  position: relative;
  z-index: 2;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.8);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 1rem;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-bottom: 4rem;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ContactInfo = styled.div`
  position: relative;
  z-index: 2;
`;

const InfoTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  transition: background-color 0.2s ease;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: rgba(255, 255, 255, 0.08);
    }
  }
  
  &:active {
    background: rgba(255, 255, 255, 0.1);
    transform: none;
  }
`;

const InfoIcon = styled.div`
  background: linear-gradient(135deg, #8B0000, #FFD700);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
`;

const InfoValue = styled.div`
  color: #ffffff;
  font-weight: 600;
  font-size: 1rem;
`;

const InfoLink = styled.a`
  color: #FFD700;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s ease;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: #FFC300;
      text-decoration: underline;
    }
  }
  
  &:active {
    color: #FFC300;
  }
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }
`;

const ActionCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: rgba(255, 255, 255, 0.08);
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
    }
  }
  
  &:active {
    background: rgba(255, 255, 255, 0.12);
    transform: none;
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    min-height: 140px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`;

const ActionIcon = styled.div`
  background: linear-gradient(135deg, #DC2626, #EF4444);
  color: white;
  width: 60px;
  height: 60px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  transition: transform 0.1s ease;
  
  ${ActionCard}:active & {
    transform: scale(0.95);
  }
`;

const ActionTitle = styled.h4`
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ActionDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  line-height: 1.4;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

// Add modal styles
const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background: rgba(15, 15, 35, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 0;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 1rem;
`;

const ModalTitle = styled.h3`
  color: #ffffff;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ModalContent = styled.div`
  padding: 0 2rem 2rem;
`;

const ContactSection: React.FC = () => {
  const [isFranchiseModalOpen, setIsFranchiseModalOpen] = useState(false);

  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'call':
        window.location.href = 'tel:+13133153014';
        break;
      case 'directions':
        const address = "4163 Cass Ave, Detroit, MI 48201";
        const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
        window.open(googleMapsUrl, '_blank');
        break;
      case 'order':
        document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth' });
        break;
      case 'franchise':
        setIsFranchiseModalOpen(true);
        break;
    }
  };

  const closeFranchiseModal = () => {
    setIsFranchiseModalOpen(false);
  };

  const handleFranchiseSuccess = () => {
    alert('Thank you for your franchise inquiry! We will contact you within 24 hours.');
    closeFranchiseModal();
  };

  // Handle escape key to close modal
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFranchiseModalOpen) {
        closeFranchiseModal();
      }
    };

    if (isFranchiseModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isFranchiseModalOpen]);

  return (
    <ContactSectionContainer id="contact">
      <Container>
        <SectionHeader>
          <SectionTitle>Get in Touch</SectionTitle>
          <SectionSubtitle>
            Ready to experience Detroit's #1 burger destination? We're here to help with orders, 
            reservations, catering, and franchise opportunities.
          </SectionSubtitle>
        </SectionHeader>

        <ContactGrid>
          <ContactInfo>
            <InfoTitle>
              <MapPin size={24} />
              Visit Us
            </InfoTitle>
            
            <InfoItem>
              <InfoIcon>
                <MapPin size={20} />
              </InfoIcon>
              <InfoContent>
                <InfoLabel>Address</InfoLabel>
                <InfoValue>4163 Cass Ave, Detroit, MI 48201</InfoValue>
              </InfoContent>
            </InfoItem>

            <InfoItem>
              <InfoIcon>
                <Phone size={20} />
              </InfoIcon>
              <InfoContent>
                <InfoLabel>Phone</InfoLabel>
                <InfoValue>
                  <InfoLink href="tel:+13133153014">(313) 315-3014</InfoLink>
                </InfoValue>
              </InfoContent>
            </InfoItem>

            <InfoItem>
              <InfoIcon>
                <Mail size={20} />
              </InfoIcon>
              <InfoContent>
                <InfoLabel>Email</InfoLabel>
                <InfoValue>
                  <InfoLink href="mailto:royalewithcheese313@gmail.com">
                    royalewithcheese313@gmail.com
                  </InfoLink>
                </InfoValue>
              </InfoContent>
            </InfoItem>

            <InfoItem>
              <InfoIcon>
                <Clock size={20} />
              </InfoIcon>
              <InfoContent>
                <InfoLabel>Hours</InfoLabel>
                <InfoValue>
                  Mon-Thu: 11 AM - 10 PM<br />
                  Fri: 11 AM - 11 PM<br />
                  Sat: 12 PM - 11 PM<br />
                  Sun: 12 PM - 6 PM
                </InfoValue>
              </InfoContent>
            </InfoItem>

            <InfoItem>
              <InfoIcon>
                <Instagram size={20} />
              </InfoIcon>
              <InfoContent>
                <InfoLabel>Follow Us</InfoLabel>
                <InfoValue>
                  <InfoLink href="https://www.instagram.com/royalewithcheese313" target="_blank" rel="noopener noreferrer">
                    @royalewithcheese313
                  </InfoLink>
                </InfoValue>
              </InfoContent>
            </InfoItem>
          </ContactInfo>

          <ContactForm 
            formType="contact"
            title="Send us a Message"
            subtitle="Have a question, feedback, or special request? We'd love to hear from you!"
          />
        </ContactGrid>

        <QuickActions>
          <ActionCard onClick={() => handleQuickAction('call')}>
            <ActionIcon>
              <Phone size={24} />
            </ActionIcon>
            <ActionTitle>Call Store</ActionTitle>
            <ActionDescription>
              Speak directly with our team for immediate assistance
            </ActionDescription>
          </ActionCard>

          <ActionCard onClick={() => handleQuickAction('directions')}>
            <ActionIcon>
              <MapPin size={24} />
            </ActionIcon>
            <ActionTitle>Get Directions</ActionTitle>
            <ActionDescription>
              Find us easily in the heart of Detroit's Midtown
            </ActionDescription>
          </ActionCard>

          <ActionCard onClick={() => handleQuickAction('order')}>
            <ActionIcon>
              <MessageSquare size={24} />
            </ActionIcon>
            <ActionTitle>Order Online</ActionTitle>
            <ActionDescription>
              Browse our menu and place your order for pickup or delivery
            </ActionDescription>
          </ActionCard>

          <ActionCard onClick={() => handleQuickAction('franchise')}>
            <ActionIcon>
              <Users size={24} />
            </ActionIcon>
            <ActionTitle>Franchise Info</ActionTitle>
            <ActionDescription>
              Learn about joining the Royale with Cheese empire
            </ActionDescription>
          </ActionCard>
        </QuickActions>
      </Container>

      {/* Franchise Modal */}
      <ModalOverlay isOpen={isFranchiseModalOpen} onClick={closeFranchiseModal}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>🚀 Join Royal Empire</ModalTitle>
            <CloseButton onClick={closeFranchiseModal}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>
          <ModalContent>
            <ContactForm 
              formType="franchise"
              title=""
              subtitle="Ready to own your piece of the Royale with Cheese empire? Our franchise team will contact you within 24 hours."
              onSuccess={handleFranchiseSuccess}
            />
          </ModalContent>
        </ModalContainer>
      </ModalOverlay>
    </ContactSectionContainer>
  );
};

export default ContactSection; 